/*
 * Переключатель брендов: знак в шапке — кнопка, по нажатию — меню знаков (docs/multi-brand.md §6).
 *
 * Кнопка несёт глобальный класс `.brand` (base.css: флекс, промежуток 10px) — им же шапка на
 * телефоне задаёт высоту строки (`Shell.module.css`), поэтому знак стоит на прежнем месте и с
 * прежней геометрией. Здесь — только то, что делает из блока кнопку: сброс, наведение, шеврон.
 */

/*
 * ШИРИНЫ ШАПКЕ ПЕРЕКЛЮЧАТЕЛЬ НЕ ПРИБАВЛЯЕТ (снято линейкой в Chromium 14.10.2026, ширины
 * 1920…360, идущий прогон и длинное имя). Запаса у лестницы подписей нет: на 1600 он 19px, а
 * шеврон с промежутком стоит 18px. Поэтому шеврон занимает часть промежутка 28px между знаком
 * и вкладками (`gap` у `.hdrInner`, Shell.module.css): обёртка отдаёт его отрицательным полем,
 * и до первой вкладки остаётся 10px. На ступени 1099 промежуток шапки становится 14px —
 * отдавать нечего, и шеврон гаснет вместе с «воздухом»: кнопкой остаётся сам знак (наведение,
 * курсор, подсказка «Сменить бренд»). Кнопка же поля не прибавляет: её отступы погашены
 * отрицательными полями `.btn`.
 */
.wrap {
  position: relative;
  display: flex;
  align-items: center;
  flex: none;
  min-width: 0;
  margin-right: -18px;
}

.btn {
  background: none;
  border: none;
  margin: 0 -8px;
  padding: 6px 8px;
  border-radius: var(--r-ctl);
  font: inherit;
  color: inherit;
  cursor: pointer;
  transition: background-color 0.12s var(--ease);
}
.btn:hover,
.btnOn {
  background: var(--bg-alt);
}
.btn:focus-visible {
  outline: 2px solid var(--violet);
  outline-offset: 0;
}

/* Шеврон — аффорданс «здесь раскроется», тише знака: он не часть бренда. Промежуток до знака
   у́же, чем между частями знака (`.brand` даёт 10px), — шеврон прижат к вордмарку. */
.chev {
  flex: none;
  margin-left: -4px;
  color: var(--muted);
  transition: transform 0.12s var(--ease);
}
.btnOn .chev {
  transform: rotate(180deg);
}

/* Ступень 1099 лестницы подписей шапки (Shell.module.css): промежутки 28 → 14px. */
@media (max-width: 1099px) {
  .wrap {
    margin-right: 0;
  }
  .chev {
    display: none;
  }
}

/* ── меню ─────────────────────────────────────────────────────────────────── */

/* Геометрия и вид — как у поповера ⓘ (`InfoPopover.module.css`) и `.tip` органов шапки:
   один механизм «всплывающее над шапкой» одной формы. Слой — выше подсказок шапки (30) и
   ниже диалога (40): открытый поверх меню диалог подтверждения ухода должен его закрывать. */
.pop {
  position: fixed;
  z-index: 35;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
  border-radius: var(--r-card);
  background: var(--white);
  border: 1px solid var(--border);
  box-shadow: var(--sh-float);
  max-height: calc(100dvh - 80px);
  overflow-y: auto;
}

.head {
  padding: 2px 4px 0;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--muted);
}

.status {
  padding: 4px;
  font-size: 12.5px;
  line-height: 1.45;
  color: var(--ink-2);
}

.menu {
  display: flex;
  flex-direction: column;
  gap: 4px;
  outline: none;
}

/* Пункт — плашка знака и подпись под ней. Ссылка, а не кнопка (см. BrandSwitcher.tsx), поэтому
   цвет держится и под курсором: `a:hover` из base.css красил бы подпись в цвет акцента. */
.item,
.item:hover {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 6px;
  border-radius: var(--r-ctl);
  color: var(--ink);
  text-decoration: none;
}
.item:hover {
  background: var(--bg-alt);
}
.item:focus-visible {
  outline: 2px solid var(--violet);
  outline-offset: -2px;
}

/*
 * Плашка — сцена своей темы. `data-theme` на ней переопределяет токены (tokens.css объявляет
 * темы по атрибуту на любом элементе), поэтому `--bg`, `--border`, `--violet` и гарнитура здесь —
 * бренда пункта, а не страницы: GeoBoost на светлом, AGI Boardroom на чёрном при любой теме.
 * Гарнитура и цвет объявлены явно: унаследованные значения пришли бы со страницы уже
 * вычисленными и токенов плашки не увидели бы.
 */
.tile {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 52px;
  padding: 0 16px;
  border-radius: var(--r-ctl);
  background: var(--bg);
  border: 1px solid var(--border);
  font-family: var(--sans);
  color: var(--ink);
  transition: border-color 0.12s var(--ease);
}
.item:hover .tile {
  border-color: var(--violet-bg-2);
}
/* Открытый бренд — кольцо акцентом СВОЕЙ темы: фиолетовым у GeoBoost, оранжевым у AGI. */
.item[aria-checked="true"] .tile {
  border-color: var(--violet);
  box-shadow: 0 0 0 1px var(--violet);
}

.cap {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 4px 2px;
  min-width: 0;
}

.title {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  color: var(--ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cur {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  color: var(--accent-ink);
}

@media (prefers-reduced-motion: reduce) {
  .btn,
  .chev,
  .tile {
    transition: none;
  }
}
